import { useCallback, useEffect, useState } from 'react';

/**
 * Returns whether a certain key is pressed or not.
 */
export function useKeyPressed(targetKey: string) {
    const [keyPressed, setKeyPressed] = useState(false);

    const downHandler = useCallback(
        ({ key }: KeyboardEvent) => {
            if (key === targetKey) {
                setKeyPressed(true);
            }
        },
        [targetKey]
    );

    const upHandler = useCallback(
        ({ key }: KeyboardEvent) => {
            if (key === targetKey) {
                setKeyPressed(false);
            }
        },
        [targetKey]
    );

    /**
     * This focus handler is necessary especially for Ctrl modifiers because they can
     * trigger browser events that remove focus from our app, and thus never fire
     * our keyup handler.
     */
    const focusHandler = () => {
        setKeyPressed(false);
    };

    useEffect(() => {
        window.addEventListener('keydown', downHandler);
        window.addEventListener('keyup', upHandler);
        window.addEventListener('focus', focusHandler);
        return () => {
            window.removeEventListener('keydown', downHandler);
            window.removeEventListener('keyup', upHandler);
            window.removeEventListener('focus', focusHandler);
        };
    }, [downHandler, upHandler]);

    return keyPressed;
}
